import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useCopy } from "@/hooks/useCopy";
import Breadcrumb from "@/components/base/Breadcrumb";

const SUBMIT_URL = "https://readdy.ai/api/form/db8bm1s6oi3g3tv8ul00";
const CONTACT_EMAIL = "hello@huangse-online.demo";

interface FormResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: unknown };
}

export default function AboutPage() {
  const { copied, copy } = useCopy();
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  usePageMeta({
    title: "关于我们 - 黄色在线",
    description:
      "了解黄色在线的网站定位、品牌故事与服务范围。黄色在线是综合影视资源与资讯展示平台，提供电影、电视剧、综艺展示与影视资讯更新。",
    keywords: "黄色在线,关于我们,影视平台,影视资讯,品牌介绍",
  });

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    setFormError("");
    const formData = new FormData(form);
    const honeypot = String(formData.get("website_alt") || "").trim();

    if (honeypot) {
      form.reset();
      setStatus("success");
      return;
    }

    const name = String(formData.get("name") || "").trim();
    const email = String(formData.get("email") || "").trim();
    const type = String(formData.get("type") || "").trim();
    const message = String(formData.get("message") || "").trim();

    if (!name || !email || !message) {
      setStatus("error");
      setFormError("请填写昵称、邮箱和反馈内容后再提交。");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus("error");
      setFormError("邮箱格式看起来不太对，请检查后再试。");
      return;
    }
    if (message.length > 500) {
      setStatus("error");
      setFormError("反馈内容不能超过 500 个字。");
      return;
    }

    const params = new URLSearchParams();
    params.append("name", name);
    params.append("email", email);
    if (type) params.append("type", type);
    params.append("message", message);

    setStatus("submitting");
    try {
      const res = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const responseText = await res.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }
      const detail = parsed?.meta?.detail;
      const rawDetail = typeof detail === "string" ? detail : "";
      const serverMsg =
        parsed?.meta?.message || parsed?.message || rawDetail || responseText || "";
      const isSpam = /spam/i.test(String(serverMsg));

      if (res.ok && parsed?.code === "OK" && !isSpam) {
        form.reset();
        setStatus("success");
      } else {
        setStatus("error");
        setFormError(serverMsg ? String(serverMsg) : "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <div className="w-full">
      <section className="w-full bg-primary-900 px-4 py-8 md:px-8 md:py-12">
        <Breadcrumb light items={[{ label: "关于我们" }]} />
        <h1 className="mt-4 font-heading text-2xl font-extrabold text-background-50 md:text-4xl">
          关于黄色在线
        </h1>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-background-300 md:text-base">
          我们相信，找到一部想看的作品，本身就是一件快乐的事。黄色在线希望把这份快乐，做得更简单一点。
        </p>
      </section>

      {/* Brand story */}
      <section className="w-full px-4 py-10 md:px-8 md:py-14">
        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-2 lg:gap-12">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-700">
              <i className="ri-quill-pen-line" aria-hidden="true" />
              品牌故事
            </span>
            <h2 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              做一份认真整理的影视目录
            </h2>
            <p className="mt-4 text-sm leading-7 text-foreground-600 md:text-base">
              黄色在线是一个综合影视资源与资讯展示平台。我们聚合电影、电视剧、综艺等热门作品，
              按类别、地区、年份整理成清晰的目录，同时持续更新影视行业动态，
              希望让每一位影视爱好者都能快速找到想看的内容。
            </p>
            <p className="mt-4 text-sm leading-7 text-foreground-600 md:text-base">
              我们不做播放与下载，也不提供付费内容，只专注于一件事：
              把信息整理得足够清楚，让你在打开页面的那一刻，就知道该看什么。
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/category/movie"
                className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-accent-600"
              >
                <i className="ri-compass-3-line" aria-hidden="true" />
                浏览影视分类
              </Link>
              <Link
                to="/news/industry"
                className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-600 transition-colors hover:border-accent-300 hover:text-accent-600"
              >
                <i className="ri-newspaper-line" aria-hidden="true" />
                浏览影视资讯
              </Link>
            </div>
          </div>
          <div className="w-full overflow-hidden rounded-lg bg-secondary-200" style={{ aspectRatio: "4 / 3" }}>
            <img
              src="https://readdy.ai/api/search-image?query=Cozy%20modern%20home%20theater%20corner%20with%20warm%20ambient%20lighting%2C%20shelves%20of%20film%20reels%20and%20books%2C%20orange%20and%20navy%20tones%2C%20welcoming%20editorial%20atmosphere%2C%20no%20text&width=800&height=600&seq=about-brand&orientation=landscape"
              alt="黄色在线影视资料整理场景"
              title="关于黄色在线 - 影视资源与资讯平台"
              className="h-full w-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Service scope */}
      <section className="w-full bg-background-100 px-4 py-10 md:px-8 md:py-14">
        <h2 className="flex items-center gap-3 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
          <span className="h-6 w-1 rounded-full bg-accent-500" aria-hidden="true" />
          我们的服务范围
        </h2>
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { icon: "ri-film-line", title: "影视资源展示", desc: "覆盖电影、电视剧、综艺三大类，作品信息一目了然。" },
            { icon: "ri-filter-3-line", title: "分类与筛选", desc: "按类型、地区、年份筛选，快速定位想看的内容。" },
            { icon: "ri-search-line", title: "关键词搜索", desc: "支持按片名、演员、导演、资讯关键词检索。" },
            { icon: "ri-newspaper-line", title: "影视资讯更新", desc: "行业动态、影视评论、幕后花絮每日更新。" },
          ].map((item) => (
            <div
              key={item.title}
              className="rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-accent-300"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                <i className={`${item.icon} text-xl`} aria-hidden="true" />
              </span>
              <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">
                {item.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-500">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Contact + feedback */}
      <section className="w-full px-4 py-10 md:px-8 md:py-14">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <h2 className="flex items-center gap-3 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              <span className="h-6 w-1 rounded-full bg-accent-500" aria-hidden="true" />
              联系方式
            </h2>
            <div className="mt-5 rounded-lg border border-background-200 bg-background-50 p-5">
              <p className="text-sm text-foreground-500">合作与建议请联系</p>
              <button
                type="button"
                onClick={() => copy(CONTACT_EMAIL)}
                className="group mt-2 flex w-full cursor-pointer items-center justify-between gap-3 rounded-md bg-background-100 px-4 py-3 text-left transition-colors hover:bg-accent-50"
              >
                <span className="inline-flex min-w-0 items-center gap-2 text-sm font-medium text-foreground-800">
                  <i className="ri-mail-line text-lg text-accent-600" aria-hidden="true" />
                  <span className="truncate">{CONTACT_EMAIL}</span>
                </span>
                <span className="inline-flex flex-shrink-0 items-center gap-1 whitespace-nowrap text-xs font-medium text-accent-600">
                  <i className={copied ? "ri-check-line" : "ri-file-copy-line"} aria-hidden="true" />
                  {copied ? "已复制" : "点击复制"}
                </span>
              </button>
            </div>

            <div className="mt-4 rounded-lg border border-dashed border-background-300 bg-background-100 p-5">
              <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-900">
                <i className="ri-information-line text-accent-600" aria-hidden="true" />
                反馈入口说明
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-500">
                你可以通过右侧表单提交内容建议、资源纠错或合作咨询。我们会在收到后的
                3 个工作日内查看，并优先处理资源信息类问题。本站为演示站点，反馈数据仅用于展示流程。
              </p>
            </div>
          </div>

          <div className="lg:col-span-7">
            <h2 className="flex items-center gap-3 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              <span className="h-6 w-1 rounded-full bg-accent-500" aria-hidden="true" />
              在线反馈
            </h2>

            <form
              id="feedback-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="mt-5 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="fb-name" className="mb-1.5 block text-sm font-medium text-foreground-700">
                    昵称
                  </label>
                  <input
                    id="fb-name"
                    name="name"
                    type="text"
                    required
                    placeholder="怎么称呼你"
                    className="w-full rounded-md border border-background-300 bg-background-100 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-accent-400 focus:bg-background-50"
                  />
                </div>
                <div>
                  <label htmlFor="fb-email" className="mb-1.5 block text-sm font-medium text-foreground-700">
                    邮箱
                  </label>
                  <input
                    id="fb-email"
                    name="email"
                    type="email"
                    required
                    placeholder="you@example.com"
                    className="w-full rounded-md border border-background-300 bg-background-100 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-accent-400 focus:bg-background-50"
                  />
                </div>
              </div>

              <div className="mt-4">
                <label htmlFor="fb-type" className="mb-1.5 block text-sm font-medium text-foreground-700">
                  反馈类型
                </label>
                <select
                  id="fb-type"
                  name="type"
                  defaultValue="内容建议"
                  className="w-full cursor-pointer rounded-md border border-background-300 bg-background-100 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-accent-400 focus:bg-background-50"
                >
                  <option value="内容建议">内容建议</option>
                  <option value="资源纠错">资源纠错</option>
                  <option value="合作咨询">合作咨询</option>
                  <option value="其他">其他</option>
                </select>
              </div>

              <div className="mt-4">
                <label htmlFor="fb-message" className="mb-1.5 block text-sm font-medium text-foreground-700">
                  反馈内容 <span className="text-xs text-foreground-400">（最多 500 字）</span>
                </label>
                <textarea
                  id="fb-message"
                  name="message"
                  rows={5}
                  maxLength={500}
                  required
                  placeholder="请描述你的建议或遇到的问题…"
                  className="w-full resize-y rounded-md border border-background-300 bg-background-100 px-3 py-2.5 text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-accent-400 focus:bg-background-50"
                />
              </div>

              <input
                className="field-extra-verify"
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <div className="mt-5 flex flex-wrap items-center gap-4">
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-accent-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <i
                    className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"}
                    aria-hidden="true"
                  />
                  {status === "submitting" ? "提交中…" : "提交反馈"}
                </button>

                {status === "success" ? (
                  <span className="inline-flex items-center gap-1.5 text-sm font-medium text-accent-700">
                    <i className="ri-checkbox-circle-line" aria-hidden="true" />
                    提交成功，感谢你的反馈！
                  </span>
                ) : null}
                {status === "error" && formError ? (
                  <span className="inline-flex items-center gap-1.5 text-sm font-medium text-accent-700">
                    <i className="ri-error-warning-line" aria-hidden="true" />
                    {formError}
                  </span>
                ) : null}
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}